import React, { type FunctionComponent, useState } from 'react'
import styled from '@emotion/styled'
import { CardBody, spacing, colors, Text } from '@retailer-platform/shared-components'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { Input } from '../../../shared-styled-components/SharedStyledComponents'
import { CronExpressionDisplay } from './CronExpressionDisplay'
import { parseCustomCron } from './cronUtils'

const StyledCardBody = styled(CardBody)({
  padding: '12px',
})

const Section = styled.div({
  marginBottom: spacing.X16,
})

const HelpText = styled.div({
  marginTop: spacing.X12,
  color: colors.GRAYSCALE.X70,
  fontSize: '14px',
})

interface Props {
  onScheduleChange?: (cronExpression: string) => void
  onError?: (error: string | null) => void
  defaultValue: string
}

export const CustomSchedule: FunctionComponent<React.PropsWithChildren<Props>> = ({
  onScheduleChange,
  onError,
  defaultValue,
}) => {
  const [customCronExpression, setCustomCronExpression] = useState<string>(defaultValue)
  const i18n = useDomainMessages({
    currentCronSchedule:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.customSchedule.currentCronSchedule',
    enterCronExpression:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.customSchedule.enterCronExpression',
  })
  const { readable } = parseCustomCron(customCronExpression)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setCustomCronExpression(newValue)
    onScheduleChange?.(newValue)
  }

  return (
    <StyledCardBody>
      <Section>
        <Text size="medium">
          {customCronExpression ? i18n.currentCronSchedule : i18n.enterCronExpression}
        </Text>
        <Input
          type="text"
          value={customCronExpression}
          onChange={handleChange}
          placeholder="00 09 * * 1-5"
        />
        <HelpText>
          Format: minute hour day month weekday
          <br />
          Use &quot;*&quot; for any value, &quot;-&quot; for ranges, &quot;,&quot; for lists,
          &quot;/&quot; for steps
        </HelpText>
      </Section>

      <CronExpressionDisplay cronExpression={customCronExpression} cronDescription={readable} />
    </StyledCardBody>
  )
}
